import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import {
  BookOpen, ChevronDown, Check, Eye, PlayCircle, FileText, Clock, X, Search,
  Compass, Rocket, Users, FileCheck, Banknote,
} from 'lucide-react';
import { useDarkMode } from '../context/DarkModeContext';
import {
  ladeAnleitungen, merkeGelesen, alleGelesen, markiereErledigt, fortschritt, istNeu, ART_LABEL,
  type Anleitung, type AnleitungsKategorie,
} from '../lib/anleitungenApi';

/** Symbolname aus dem Katalog → Komponente. Unbekanntes fällt auf das Buch zurück. */
const SYMBOLE: Record<string, typeof BookOpen> = { Compass, Rocket, Users, FileCheck, Banknote, BookOpen };

/** Zuletzt geöffnete Anleitung — rein persönliche Komfortfunktion (wie der ROI-Rechner),
 *  deshalb localStorage statt Backend. Nur id+Zeitstempel: Titel/Kategorie kommen beim
 *  Anzeigen live aus dem Katalog, damit eine spätere Umbenennung nicht veraltet wird. */
const ZULETZT_KEY = 'startklar.anleitungen.zuletztAngesehen.v1';
type ZuletztAngesehen = { id: string; lastOpenedAt: number };
function ladeZuletztAngesehen(): ZuletztAngesehen | null {
  try {
    const parsed = JSON.parse(localStorage.getItem(ZULETZT_KEY) || 'null');
    if (parsed && typeof parsed.id === 'string' && typeof parsed.lastOpenedAt === 'number') return parsed;
  } catch { /* localStorage kann im Privatmodus fehlen */ }
  return null;
}
function speichereZuletztAngesehen(id: string): ZuletztAngesehen {
  const eintrag = { id, lastOpenedAt: Date.now() };
  try { localStorage.setItem(ZULETZT_KEY, JSON.stringify(eintrag)); } catch { /* s.o. */ }
  return eintrag;
}

/** Nur interne Pfade als Rücksprungziel zulassen (?zurueck= kommt aus der URL, also
 *  potenziell manipulierbar) — kein "//evil.com" (protokoll-relativ) oder "https://…". */
function gueltigerInternerPfad(wert: string | null): string | null {
  if (!wert || !wert.startsWith('/') || wert.startsWith('//') || wert.includes('://')) return null;
  return wert;
}

/** Verständliche Beschriftung für den dynamischen Rücksprung — anhand des Pfads, den
 *  HilfeLink mitgegeben hat. Fällt auf einen generischen Text zurück, wenn der Bereich
 *  (noch) keinen eigenen Hilfe-Link hat. */
function zielLabelFuerZurueck(pfad: string): string {
  if (pfad.startsWith('/editor')) return 'Zurück zum Bericht';
  if (pfad.startsWith('/customers')) return pfad.includes('customer=') ? 'Zurück zum Kunden' : 'Zurück zu den Kunden';
  if (pfad.startsWith('/reports')) return 'Zurück zu den Berichten';
  return 'Zurück zur Software';
}

function ArtSymbol({ art }: { art: Anleitung['art'] }) {
  if (art === 'video') return <PlayCircle className="w-3.5 h-3.5" />;
  if (art === 'beides') return <PlayCircle className="w-3.5 h-3.5" />;
  return <FileText className="w-3.5 h-3.5" />;
}

/**
 * Eine Anleitung im Lesefenster.
 *
 * „Angesehen" setzt das Elternteil automatisch beim Öffnen (kein Button nötig). „Abgeschlossen"
 * bleibt eine bewusste Angabe über den Button — sonst wäre jede Anleitung nach dem ersten
 * Hineinschauen sofort als erledigt verbucht. Einmal abgeschlossen gibt es kein Zurück: statt
 * eines Buttons steht dann nur noch der dezente Status da.
 */
function Lesefenster({
  a,
  erledigt,
  ziel,
  kategorieTitel,
  vorherige,
  naechste,
  onSchliessen,
  onErledigt,
  onVorherige,
  onNaechste,
}: {
  a: Anleitung;
  erledigt: boolean;
  /** Rücksprung-CTA — dynamisch (zuvor geöffnete Seite) oder statisch (a.ziel), bereits
   *  vom Elternteil aufgelöst. null = kein sinnvolles Ziel, dann erscheint kein Button. */
  ziel: { pfad: string; label: string } | null;
  kategorieTitel: string;
  /** Vorherige/nächste Anleitung in der Katalog-Reihenfolge, über Kategoriegrenzen hinweg —
   *  vom Elternteil anhand der flachen Liste bestimmt. null am jeweiligen Rand. */
  vorherige: { titel: string } | null;
  naechste: { titel: string } | null;
  onSchliessen: () => void;
  onErledigt: () => void;
  onVorherige: () => void;
  onNaechste: () => void;
}) {
  const navigate = useNavigate();
  useEffect(() => {
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') onSchliessen(); };
    document.addEventListener('keydown', esc);
    return () => document.removeEventListener('keydown', esc);
  }, [onSchliessen]);

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/50 p-4 overflow-y-auto"
      onClick={onSchliessen}
      role="dialog"
      aria-modal="true"
      aria-label={a.titel}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-3xl my-8 rounded-xl bg-white dark:bg-slate-800 shadow-2xl border border-slate-200 dark:border-slate-700"
      >
        <div className="flex items-start justify-between gap-4 px-6 py-4 border-b border-slate-200 dark:border-slate-700">
          <div className="min-w-0">
            <p className="text-xs text-slate-400 dark:text-slate-500">Anleitungen › {kategorieTitel}</p>
            <h2 className="mt-0.5 text-lg font-semibold text-slate-900 dark:text-white">{a.titel}</h2>
            <p className="mt-0.5 flex items-center gap-3 text-xs text-slate-500 dark:text-slate-400">
              <span className="inline-flex items-center gap-1"><ArtSymbol art={a.art} /> {ART_LABEL[a.art]}</span>
              <span className="inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {a.dauer} Min.</span>
            </p>
          </div>
          <button onClick={onSchliessen} aria-label="Schließen"
            className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 dark:hover:bg-slate-700 dark:hover:text-slate-200">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="px-6 py-5 space-y-5">
          {(a.art === 'video' || a.art === 'beides') && (
            a.videoUrl ? (
              <div className="aspect-video w-full overflow-hidden rounded-lg bg-black">
                <iframe src={a.videoUrl} title={a.titel} allowFullScreen className="w-full h-full" />
              </div>
            ) : (
              <div className="aspect-video w-full flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed border-slate-300 dark:border-slate-600 text-slate-400">
                <PlayCircle className="w-10 h-10" />
                <span className="text-sm">Video folgt</span>
              </div>
            )
          )}

          {a.text ? (
            // Kein `prose`: Das Typography-Plugin ist nicht installiert, die Klassen wären
            // wirkungslos — Überschriften kämen in Fließtextgröße, Listen ohne Punkte.
            // Deshalb die paar Regeln, die dieser Inhalt wirklich braucht, direkt hier.
            <div
              data-testid="anleitung-text"
              className="text-sm leading-relaxed text-slate-700 dark:text-slate-200
                [&>*:first-child]:mt-0
                [&_h3]:mt-6 [&_h3]:mb-2 [&_h3]:text-base [&_h3]:font-semibold
                [&_h3]:text-slate-900 dark:[&_h3]:text-white
                [&_p]:my-3
                [&_ul]:my-3 [&_ul]:pl-5 [&_ul]:list-disc [&_ul]:space-y-1.5
                [&_ol]:my-3 [&_ol]:pl-5 [&_ol]:list-decimal [&_ol]:space-y-1.5
                [&_li]:pl-1 [&_li>ul]:mt-1.5 [&_li>ol]:mt-1.5
                [&_strong]:font-semibold [&_strong]:text-slate-900 dark:[&_strong]:text-white
                [&_em]:italic [&_em]:text-slate-500 dark:[&_em]:text-slate-400
                [&_code]:rounded [&_code]:bg-slate-100 dark:[&_code]:bg-slate-900/60
                [&_code]:px-1.5 [&_code]:py-0.5 [&_code]:text-[12px]
                [&_code]:text-slate-700 dark:[&_code]:text-slate-300
                [&_a]:text-blue-600 dark:[&_a]:text-blue-400 [&_a]:underline"
              dangerouslySetInnerHTML={{ __html: a.text }}
            />
          ) : null}

          {a.bilder?.map((bild, i) => (
            <img key={i} src={bild} alt={`${a.titel} – Abbildung ${i + 1}`} className="w-full rounded-lg border border-slate-200 dark:border-slate-700" />
          ))}
        </div>

        {(vorherige || naechste) && (
          <div className="flex items-center justify-between gap-3 px-6 py-3 border-t border-slate-100 dark:border-slate-700/60 text-sm">
            {vorherige ? (
              <button
                data-testid="lesefenster-vorherige"
                onClick={onVorherige}
                className="inline-flex items-center gap-1.5 max-w-[46%] truncate text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white"
              >
                ← {vorherige.titel}
              </button>
            ) : <span />}
            {naechste ? (
              <button
                data-testid="lesefenster-naechste"
                onClick={onNaechste}
                className="inline-flex items-center gap-1.5 max-w-[46%] truncate text-right text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white"
              >
                {naechste.titel} →
              </button>
            ) : <span />}
          </div>
        )}

        <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-slate-200 dark:border-slate-700">
          {erledigt ? (
            <span className="inline-flex items-center gap-1.5 text-sm text-green-700 dark:text-green-400">
              <Check className="w-4 h-4" /> Abgeschlossen
            </span>
          ) : (
            <span className="text-sm text-slate-500 dark:text-slate-400">Noch nicht abgeschlossen</span>
          )}
          <div className="flex items-center gap-2">
            {ziel && (
              <button
                onClick={() => { onSchliessen(); navigate(ziel.pfad); }}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-lg border border-slate-300 dark:border-slate-600 text-slate-600 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700"
              >
                {ziel.label} →
              </button>
            )}
            {!erledigt && (
              <button onClick={onErledigt}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium rounded-lg bg-green-600 hover:bg-green-700 text-white">
                <Check className="w-4 h-4" /> Als erledigt markieren
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/** Eine Anleitungs-Kachel — von KategorieBlock UND der Such-Trefferliste genutzt, damit
 *  beide optisch identisch bleiben. */
function AnleitungKachel({
  a, gelesen, erledigt, onOeffnen,
}: { a: Anleitung; gelesen: Set<string>; erledigt: Set<string>; onOeffnen: (a: Anleitung) => void }) {
  const ist = erledigt.has(a.id);
  const nurAngesehen = !ist && gelesen.has(a.id);
  // Noch kein Video hinterlegt (Kundenwunsch 16.10.2026: alle Anleitungen sind für Video+Text
  // vorgesehen, aber erst Texte existieren) — Kachel soll das nicht als "Video"/"Text + Video"
  // verkaufen, sondern ehrlich "Video folgt" zeigen, wie es das Lesefenster schon tut.
  const videoFehlt = (a.art === 'video' || a.art === 'beides') && !a.videoUrl;
  return (
    <button
      onClick={() => onOeffnen(a)}
      data-testid="anleitung-eintrag"
      className={`group h-full flex flex-col text-left rounded-lg border overflow-hidden transition hover:shadow-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
        ist
          ? 'border-green-300 dark:border-green-800 bg-green-50/40 dark:bg-green-900/10'
          : 'border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800'
      }`}
    >
      {/* Vorschaufläche: solange kein Bild hinterlegt ist, ein erkennbarer
          Platzhalter statt einer leeren grauen Box — der sagt wenigstens,
          ob es ein Text oder ein Video wird. */}
      {/* Feste Höhe statt Seitenverhältnis: Die Kacheln einer Reihe sind so hoch
          wie die längste — ein mitwachsendes Bild ließe darüber oder darunter
          ungleiche Lücken stehen. */}
      <div className="relative h-36 w-full shrink-0 overflow-hidden bg-slate-100 dark:bg-slate-900/40">
        {a.vorschau ? (
          <img
            src={a.vorschau}
            alt=""
            loading="lazy"
            className="w-full h-full object-cover transition-transform group-hover:scale-[1.03]"
          />
        ) : (
          <div className={`w-full h-full flex flex-col items-center justify-center gap-1.5 ${
            a.art === 'text'
              ? 'bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-800 dark:to-slate-900 text-slate-400'
              : 'bg-gradient-to-br from-amber-100 to-amber-200 dark:from-amber-900/40 dark:to-amber-950/40 text-amber-600 dark:text-amber-400'
          }`}>
            {a.art === 'text' ? <FileText className="w-8 h-8" /> : <PlayCircle className="w-9 h-9" />}
            <span className="text-[11px] font-medium">{videoFehlt ? 'Video folgt' : ART_LABEL[a.art]}</span>
          </div>
        )}

        {ist && (
          <span
            title="Abgeschlossen"
            className="absolute top-2 right-2 w-6 h-6 rounded-full bg-green-600 text-white flex items-center justify-center shadow"
          >
            <Check className="w-3.5 h-3.5" />
          </span>
        )}
        {nurAngesehen && (
          <span
            title="Angesehen"
            className="absolute top-2 right-2 w-6 h-6 rounded-full bg-slate-500/90 text-white flex items-center justify-center shadow"
          >
            <Eye className="w-3.5 h-3.5" />
          </span>
        )}
        {istNeu(a, gelesen) && (
          <span className="absolute top-2 left-2 text-[11px] font-semibold px-1.5 py-0.5 rounded-full bg-blue-600 text-white shadow">
            Neu
          </span>
        )}
        <span className="absolute bottom-2 right-2 inline-flex items-center gap-1 text-[11px] font-medium px-1.5 py-0.5 rounded-full bg-black/60 text-white">
          <Clock className="w-3 h-3" /> {a.dauer}′
        </span>
      </div>

      {/* Zwei Zeilen Titel, zwei Zeilen Beschreibung — feste Höhen, damit kurze
          und lange Titel dieselbe Kachel ergeben. */}
      <div className="p-3 flex-1 flex flex-col">
        <h3 className="text-sm font-medium text-slate-900 dark:text-white line-clamp-2 min-h-[2.5rem] leading-tight">
          {a.titel}
        </h3>
        <p className="mt-1 text-xs text-slate-500 dark:text-slate-400 line-clamp-2 min-h-[2rem] leading-tight">
          {a.kurz}
        </p>
      </div>
    </button>
  );
}

function KategorieBlock({
  k,
  offen,
  onToggle,
  gelesen,
  erledigt,
  onOeffnen,
}: {
  k: AnleitungsKategorie;
  offen: boolean;
  onToggle: () => void;
  gelesen: Set<string>;
  erledigt: Set<string>;
  onOeffnen: (a: Anleitung) => void;
}) {
  const Symbol = SYMBOLE[k.symbol] ?? BookOpen;
  const stand = fortschritt(k, erledigt);
  const fertig = stand.gelesen === stand.gesamt;

  return (
    <div data-testid="anleitung-kategorie" className="rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 overflow-hidden">
      <div
        role="button"
        tabIndex={0}
        onClick={onToggle}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle(); } }}
        className="w-full flex items-center gap-4 px-4 sm:px-5 py-4 text-left cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700/40 transition-colors"
      >
        <div className={`w-11 h-11 rounded-lg flex items-center justify-center shrink-0 ${
          fertig ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                 : 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400'
        }`}>
          <Symbol className="w-5 h-5" />
        </div>
        <div className="min-w-0 flex-1">
          <h2 className="text-base font-semibold text-slate-900 dark:text-white">{k.titel}</h2>
          <p className="text-sm text-slate-500 dark:text-slate-400 truncate">{k.beschreibung}</p>
        </div>
        <span className="shrink-0 text-xs font-medium text-slate-500 dark:text-slate-400 whitespace-nowrap">
          {stand.gelesen}/{stand.gesamt}
        </span>
        <ChevronDown className={`w-4 h-4 text-slate-400 shrink-0 transition-transform ${offen ? '' : '-rotate-90'}`} />
      </div>

      {offen && (
        <div className="border-t border-slate-100 dark:border-slate-700/60 p-4 sm:p-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-3 items-stretch">
          {k.anleitungen.map((a) => (
            <AnleitungKachel key={a.id} a={a} gelesen={gelesen} erledigt={erledigt} onOeffnen={onOeffnen} />
          ))}
        </div>
      )}
    </div>
  );
}

export default function Anleitungen() {
  const { darkMode } = useDarkMode();
  const [kategorien, setKategorien] = useState<AnleitungsKategorie[] | null>(null);
  // "gelesen" = angesehen ODER abgeschlossen (jede Zeile in der DB); "erledigt" nur die aktiv
  // über "Als erledigt markieren" bestätigten — siehe anleitungenApi.ts.
  const [gelesen, setGelesen] = useState<Set<string>>(new Set());
  const [erledigt, setErledigt] = useState<Set<string>>(new Set());
  const [offen, setOffen] = useState<Record<string, boolean>>({});
  const [lesen, setLesen] = useState<Anleitung | null>(null);
  // Nur für den aktuell offenen Lesefenster-Aufruf gültig (siehe oeffneAnleitung) — wird nie
  // für spätere Aufrufe wiederverwendet, damit ein alter Arbeitskontext nicht versehentlich
  // an eine andere Anleitung "klebt", wenn man sie später aus der Übersicht öffnet.
  const [lesenZurueck, setLesenZurueck] = useState<string | null>(null);
  const [suche, setSuche] = useState('');
  const [zuletzt, setZuletzt] = useState<ZuletztAngesehen | null>(() => ladeZuletztAngesehen());
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    ladeAnleitungen()
      .then((d) => {
        setKategorien(d.kategorien);
        setGelesen(new Set(d.gelesen));
        setErledigt(new Set(d.erledigt ?? []));
        // Die erste Kategorie offen: Eine Seite aus lauter zugeklappten Balken sagt nicht,
        // was einen erwartet.
        setOffen(d.kategorien.length ? { [d.kategorien[0].id]: true } : {});
      })
      .catch(() => setKategorien([]));
  }, []);

  const gesamt = useMemo(
    () => (kategorien ?? []).reduce((n, k) => n + k.anleitungen.length, 0),
    [kategorien],
  );
  const offenAnzahl = gesamt - gelesen.size;

  // Angesehen wird automatisch gesetzt, sobald eine Anleitung im Lesefenster offen ist — kein
  // Button nötig. Nur ein Auffrischen, wenn sie nicht schon als angesehen/abgeschlossen zählt;
  // ein erneutes Öffnen einer bereits abgeschlossenen Anleitung rührt daher nichts an.
  useEffect(() => {
    if (!lesen || gelesen.has(lesen.id)) return;
    const id = lesen.id;
    setGelesen((prev) => new Set(prev).add(id));
    merkeGelesen(id).catch(() => setGelesen((prev) => {
      const zurueck = new Set(prev);
      zurueck.delete(id);
      return zurueck;
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lesen?.id]);

  // Suche über Titel, Kurzbeschreibung und den vollen Anleitungstext — alles liegt schon
  // aus ladeAnleitungen() im Speicher, keine eigene Abfrage nötig. Treffer kategorieübergreifend
  // als flaches Raster, damit man nicht erst die richtige Kategorie aufklappen muss (Kundenwunsch
  // 16.10.2026, 22 Anleitungen sind sonst schwer zu überblicken).
  const sucheNormiert = suche.trim().toLowerCase();
  const treffer = useMemo(() => {
    if (!sucheNormiert) return [];
    return (kategorien ?? []).flatMap((k) => k.anleitungen).filter((a) => {
      const textOhneHtml = (a.text ?? '').replace(/<[^>]*>/g, ' ');
      return `${a.titel} ${a.kurz} ${textOhneHtml}`.toLowerCase().includes(sucheNormiert);
    });
  }, [kategorien, sucheNormiert]);

  // Zuletzt geöffnete Anleitung live aus dem Katalog nachschlagen (nicht aus dem Speicher-
  // Eintrag selbst) — so verschwindet die Karte sauber, falls die Anleitung inzwischen
  // gelöscht wurde, statt einen veralteten Titel zu zeigen.
  const zuletztRef = useMemo(() => {
    if (!zuletzt || !kategorien) return null;
    for (const k of kategorien) {
      const a = k.anleitungen.find((x) => x.id === zuletzt.id);
      if (a) return { a, kategorie: k };
    }
    return null;
  }, [zuletzt, kategorien]);

  // `zurueck`: nur gesetzt, wenn der Aufruf über einen Deep-Link mit gültigem Rücksprungziel
  // kam (siehe Deep-Link-Effekt unten). Ein normaler Klick auf eine Kachel ruft diese Funktion
  // ohne zweites Argument auf → kein alter Kontext aus einem früheren Deep-Link bleibt hängen.
  const oeffneAnleitung = (a: Anleitung, zurueck: string | null = null) => {
    setLesen(a);
    setLesenZurueck(zurueck);
    setZuletzt(speichereZuletztAngesehen(a.id));
  };

  const entferneAnleitungParam = () => {
    if (!searchParams.get('anleitung') && !searchParams.get('zurueck')) return;
    setSearchParams((prev) => {
      const naechste = new URLSearchParams(prev);
      naechste.delete('anleitung');
      naechste.delete('zurueck');
      return naechste;
    }, { replace: true });
  };

  // Deep-Link /anleitungen?anleitung=<id>&zurueck=<pfad> (Hilfe-Icons aus der übrigen Software
  // öffnen damit direkt die passende Anleitung — auch bei direktem Aufruf/Neuladen, weil dieser
  // Effekt erst greift, sobald kategorien geladen sind). Reagiert auch, wenn der Parameter
  // sich ändert während die Seite schon offen ist, und wechselt dann das Lesefenster um.
  // Unbekannte ID: Parameter verwerfen, normale Übersicht zeigen statt hängen zu bleiben.
  useEffect(() => {
    const id = searchParams.get('anleitung');
    if (!id || !kategorien) return;
    for (const k of kategorien) {
      const a = k.anleitungen.find((x) => x.id === id);
      if (a) {
        oeffneAnleitung(a, gueltigerInternerPfad(searchParams.get('zurueck')));
        setOffen((p) => ({ ...p, [k.id]: true }));
        return;
      }
    }
    entferneAnleitungParam();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, kategorien]);

  // Dynamischer Rücksprung hat Vorrang vor dem statischen a.ziel aus dem Katalog; ganz ohne
  // sinnvolles Ziel bleibt es null → Lesefenster zeigt dann keinen Rücksprung-Button.
  const lesenZiel = useMemo(() => {
    if (lesenZurueck) return { pfad: lesenZurueck, label: zielLabelFuerZurueck(lesenZurueck) };
    return lesen?.ziel ?? null;
  }, [lesen, lesenZurueck]);

  // Flache Liste in Katalog-Reihenfolge (Kategorien wie geladen, darin wie hinterlegt) — Basis
  // für "Vorherige"/"Nächste" im Lesefenster, funktioniert so auch über Kategoriegrenzen hinweg.
  const flacheListe = useMemo(
    () => (kategorien ?? []).flatMap((k) => k.anleitungen.map((a) => ({ a, kategorie: k }))),
    [kategorien],
  );
  const lesenIndex = lesen ? flacheListe.findIndex((e) => e.a.id === lesen.id) : -1;
  const lesenKategorie = lesenIndex >= 0 ? flacheListe[lesenIndex].kategorie : null;
  const vorherige = lesenIndex > 0 ? flacheListe[lesenIndex - 1] : null;
  const naechste = lesenIndex >= 0 && lesenIndex < flacheListe.length - 1 ? flacheListe[lesenIndex + 1] : null;

  // Beim Blättern bleibt ein vorhandener dynamischer Rücksprungkontext erhalten (man ist ja
  // weiterhin in derselben Anleitungs-Session) — nur ein neuer Klick auf eine Kachel setzt ihn
  // bewusst zurück (oeffneAnleitung ohne zweites Argument).
  const geheZu = (ziel: { a: Anleitung; kategorie: AnleitungsKategorie }) => {
    oeffneAnleitung(ziel.a, lesenZurueck);
    setOffen((p) => ({ ...p, [ziel.kategorie.id]: true }));
  };

  // "Als erledigt markieren" — angesehen ist in diesem Moment immer schon gesetzt (siehe
  // Auto-Effekt oben), eine abgeschlossene Anleitung impliziert also "gelesen" mit.
  const markiereAlsErledigt = (id: string) => {
    setErledigt((prev) => new Set(prev).add(id));
    setGelesen((prev) => new Set(prev).add(id));
    markiereErledigt(id).catch(() => setErledigt((prev) => {
      const zurueck = new Set(prev);
      zurueck.delete(id);
      return zurueck;
    }));
  };

  const alleAlsGelesen = () => {
    const vorher = new Set(gelesen);
    setGelesen(new Set((kategorien ?? []).flatMap((k) => k.anleitungen.map((a) => a.id))));
    alleGelesen().catch(() => setGelesen(vorher));
  };

  return (
    <div className={`min-h-screen ${darkMode ? 'bg-slate-900' : 'bg-slate-50'}`}>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        <div className="flex items-start gap-4 mb-6">
          <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-amber-500 to-amber-600 flex items-center justify-center shrink-0">
            <BookOpen className="w-6 h-6 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl font-semibold text-slate-900 dark:text-white">Anleitungen</h1>
            <p className="text-sm text-slate-600 dark:text-slate-400 mt-1">
              Kurz erklärt, wie die BAFA-Förderung läuft und wie Sie sie hier abwickeln.
            </p>
            {gesamt > 0 && (
              <p className="flex items-center gap-1.5 flex-wrap text-xs text-slate-500 dark:text-slate-400 mt-1.5">
                <span>{erledigt.size} von {gesamt} Anleitungen abgeschlossen</span>
                {offenAnzahl > 0 && (
                  <>
                    <span aria-hidden="true">·</span>
                    <button onClick={alleAlsGelesen} className="text-blue-600 dark:text-blue-400 hover:underline">
                      Alle als durchgesehen markieren
                    </button>
                  </>
                )}
              </p>
            )}
          </div>
        </div>

        {zuletztRef && (
          <div className="mb-6 rounded-xl border border-slate-200 dark:border-slate-700 bg-white dark:bg-slate-800 px-4 sm:px-5 py-4 flex items-center justify-between gap-4">
            <div className="min-w-0">
              <p className="inline-flex items-center gap-1.5 text-xs font-medium text-slate-500 dark:text-slate-400">
                <PlayCircle className="w-3.5 h-3.5" />
                {erledigt.has(zuletztRef.a.id) ? 'Zuletzt angesehen' : 'Weiterlernen'}
              </p>
              <h3 className="mt-1 text-sm font-semibold text-slate-900 dark:text-white truncate">{zuletztRef.a.titel}</h3>
              <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400 truncate">{zuletztRef.kategorie.titel}</p>
            </div>
            <button
              onClick={() => oeffneAnleitung(zuletztRef.a)}
              className="shrink-0 inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium rounded-full bg-slate-900 hover:bg-slate-800 text-white dark:bg-white dark:text-slate-900 dark:hover:bg-slate-100"
            >
              {erledigt.has(zuletztRef.a.id) ? 'Öffnen' : 'Fortsetzen'} →
            </button>
          </div>
        )}

        {kategorien !== null && kategorien.length > 0 && (
          <div className="relative mb-6">
            <Search className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <input
              type="text"
              value={suche}
              onChange={(e) => setSuche(e.target.value)}
              placeholder="Anleitung suchen, z. B. „Bericht exportieren“…"
              className="w-full pl-10 pr-10 py-2.5 rounded-lg border border-slate-300 dark:border-slate-600 bg-white dark:bg-slate-800 text-sm text-slate-900 dark:text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
            {suche && (
              <button
                onClick={() => setSuche('')}
                aria-label="Suche zurücksetzen"
                className="absolute right-3 top-1/2 -translate-y-1/2 p-0.5 rounded text-slate-400 hover:text-slate-600 dark:hover:text-slate-200"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>
        )}

        {kategorien === null ? (
          <p className="text-sm text-slate-500 dark:text-slate-400 py-12 text-center">Lädt…</p>
        ) : kategorien.length === 0 ? (
          <p className="text-sm text-slate-500 dark:text-slate-400 py-12 text-center">Noch keine Anleitungen hinterlegt.</p>
        ) : sucheNormiert ? (
          treffer.length === 0 ? (
            <p className="text-sm text-slate-500 dark:text-slate-400 py-12 text-center">
              Keine Anleitung gefunden für „{suche.trim()}".
            </p>
          ) : (
            <div data-testid="anleitung-treffer" className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 items-stretch">
              {treffer.map((a) => (
                <AnleitungKachel key={a.id} a={a} gelesen={gelesen} erledigt={erledigt} onOeffnen={oeffneAnleitung} />
              ))}
            </div>
          )
        ) : (
          <div className="space-y-3">
            {kategorien.map((k) => (
              <KategorieBlock
                key={k.id}
                k={k}
                offen={!!offen[k.id]}
                onToggle={() => setOffen((p) => ({ ...p, [k.id]: !p[k.id] }))}
                gelesen={gelesen}
                erledigt={erledigt}
                onOeffnen={oeffneAnleitung}
              />
            ))}
          </div>
        )}
      </div>

      {lesen && (
        <Lesefenster
          a={lesen}
          erledigt={erledigt.has(lesen.id)}
          ziel={lesenZiel}
          kategorieTitel={lesenKategorie?.titel ?? ''}
          vorherige={vorherige ? { titel: vorherige.a.titel } : null}
          naechste={naechste ? { titel: naechste.a.titel } : null}
          onSchliessen={() => { setLesen(null); setLesenZurueck(null); entferneAnleitungParam(); }}
          onErledigt={() => markiereAlsErledigt(lesen.id)}
          onVorherige={() => vorherige && geheZu(vorherige)}
          onNaechste={() => naechste && geheZu(naechste)}
        />
      )}
    </div>
  );
}
